import { useRef, useEffect, useMemo } from 'react';

// Move the easing function outside the component to avoid recreation
const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

export const useChatScroll = (messages = []) => {
  // Refs
  const chatContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const scrollTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const animationFrameRef = useRef(null);

  // Memoized scroll functions
  const scrollFunctions = useMemo(() => ({
    // Basic scroll to bottom
    scrollToBottom: ({ force = false, instant = false } = {}) => {
      if (!chatContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100;

      if (force || isNearBottom) {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        const scroll = () => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
              top: chatContainerRef.current.scrollHeight,
              behavior: instant ? 'auto' : 'smooth'
            });
          }
        };

        scrollTimeoutRef.current = setTimeout(scroll, 10);
      }
    },

    // Smooth scroll with easing
    smoothScrollToBottom: (duration = 800) => {
      if (!chatContainerRef.current) return;

      const start = chatContainerRef.current.scrollTop;
      const end = chatContainerRef.current.scrollHeight - chatContainerRef.current.clientHeight;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = start + (end - start) * easeInOutQuad(progress);
        }

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animateScroll);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateScroll);
    },

    // Handle scroll events
    handleScroll: () => {
      if (!chatContainerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      isAtBottomRef.current = scrollHeight - (scrollTop + clientHeight) < 100;
    }
  }), []);

  // Removed initial auto-scroll effect from hook.
  // The chat page now controls when to scroll after history loads.


  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    chatContainerRef,
    scrollToBottom: scrollFunctions.scrollToBottom,
    smoothScrollToBottom: scrollFunctions.smoothScrollToBottom,
    handleScroll: scrollFunctions.handleScroll,
    isAtBottom: isAtBottomRef
  };
};
